import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { UniversalToggle } from '@practium/ui';
import type { Manufacturer, ProductPageMode, FertilizerFormData } from '../../../types';
import { FormSectionTitle, FormField, FormInput, FormSelect } from '@practium/ui';
import { NutrientInput } from '../../NutrientInput/NutrientInput';
import { useFertilizerForm } from '../../../hooks/useFertilizerForm';
import {
	FERTILIZER_CATEGORY_OPTIONS,
	FERTILIZER_NATURE_OPTIONS,
	MACROS,
	MICROS,
	CORRECTIVOS,
	APPLICATION_METHODS,
} from '../../../constants/fertilizer.constants';
import styles from './FertilizerForm.module.css';
import type { FormSelectOption } from '@practium/ui';
import generalStyles from '../../ProductFormCard/ProductFormCard.module.css';
import { AddManufacturerModal } from '../ManufacturerForm/AddManufacturerModal';

const ADD_NEW_MANUFACTURER = '__add_new__';

export interface FertilizerFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface FertilizerFormProps {
	manufacturers: Manufacturer[];
	mode?: ProductPageMode;
	initialData?: FertilizerFormData;
	onSubmit: () => void;
	onLoadingChange?: (loading: boolean) => void;
	onIdentifierChange?: (name: string, nature: string, manufacturer: string, extra: string) => void;
	id?: string;
	file?: File[];
}

const resolveLabel = (options: FormSelectOption[], value: string) =>
	(options.find((o) => o.value === value)?.label ?? value) || '—';

const resolveLabels = (options: FormSelectOption[], values: string[]) =>
	values.length > 0 ? values.map((value) => resolveLabel(options, value)).join(', ') : '—';

export const FertilizerForm = forwardRef<FertilizerFormRef, FertilizerFormProps>(
	function FertilizerForm(
		{ manufacturers, mode = 'add', initialData, onSubmit, onLoadingChange, onIdentifierChange, id, file },
		ref
	) {
		const [showAddManufacturerModal, setShowAddManufacturerModal] = useState(false);

		const { form, errors, isLoading, set, handleSubmit, isDirty } = useFertilizerForm({
			manufacturers,
			initialData,
			onSubmit,
			id,
			file
		});

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange?.(isLoading);
		}, [isLoading, onLoadingChange]);

		const isView = mode === 'view';
		const isLime = form.category === 'lime';
		const showsCarbon = form.category === 'special' || form.category === 'organic';
		const visibleMacros = MACROS.filter(({ key }) => key !== 'C' || showsCarbon);
		const nutrientUnit = form.nature === 'solid' ? '%' : 'g/L';

		const manufacturerOptions: FormSelectOption[] = [
			{ value: ADD_NEW_MANUFACTURER, label: '+ Adicionar novo fabricante', action: true },
			...manufacturers.map((m) => ({
				value: m._id,
				label: m.tradingName ?? m.businessName ?? '',
			})),
		];

		const triggerChange = (updatedForm: FertilizerFormData) => {
			onIdentifierChange?.(
				updatedForm.name,
				updatedForm.nature,
				resolveLabel(manufacturerOptions, updatedForm.manufacturerId),
				resolveLabel(FERTILIZER_CATEGORY_OPTIONS, updatedForm.category)
			);
		};

		const handleManufacturerChange = (v: string) => {
			if (v === ADD_NEW_MANUFACTURER) {
				setShowAddManufacturerModal(true);
				return;
			}
			set('manufacturerId', v);
			triggerChange({ ...form, manufacturerId: v });
		};

		const handleManufacturerCreated = (newId: string) => {
			setShowAddManufacturerModal(false);
			set('manufacturerId', newId);
			triggerChange({ ...form, manufacturerId: newId });
		};

		const handleCategoryChange = (category: string) => {
			set('category', category);
			if (category !== 'special' && category !== 'organic') set('C', '');
			triggerChange({ ...form, category });
		};

		const hasMacros = visibleMacros.some(({ key }) => !!form[key as keyof FertilizerFormData]);
		const hasMicros = MICROS.some(({ key }) => !!form[key as keyof FertilizerFormData]);

		return (
			<>
				{showAddManufacturerModal && (
					<AddManufacturerModal
						isOpen={showAddManufacturerModal}
						onClose={() => setShowAddManufacturerModal(false)}
						onCreated={handleManufacturerCreated}
					/>
				)}
				<div className={generalStyles.form}>
					<div className={generalStyles.section}>
						<FormSectionTitle>Informações Gerais</FormSectionTitle>
						<div className={generalStyles.sectionFields}>
							<FormField label="Identificação" required={!isView} error={errors.name}>
								{isView ? (
									<span className={generalStyles.viewText}>{form.name || '—'}</span>
								) : (
									<FormInput
										placeholder="Ex.: Ureia"
										value={form.name}
										onChange={(e) => {
											set('name', e.target.value);
											triggerChange({ ...form, name: e.target.value });
										}}
										hasError={!!errors.name}
									/>
								)}
							</FormField>
							<div className={generalStyles.row2}>
								<FormField
									label="Fabricante / Marca"
									required={!isView}
									error={errors.manufacturerId}
								>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveLabel(manufacturerOptions, form.manufacturerId)}
										</span>
									) : (
										<FormSelect
											value={form.manufacturerId}
											onChange={handleManufacturerChange}
											options={manufacturerOptions}
											placeholder="Selecione o fabricante"
											hasError={!!errors.manufacturerId}
										/>
									)}
								</FormField>
								<FormField label="Categoria" required={!isView} error={errors.category}>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveLabel(FERTILIZER_CATEGORY_OPTIONS, form.category)}
										</span>
									) : (
										<FormSelect
											value={form.category}
											onChange={handleCategoryChange}
											options={FERTILIZER_CATEGORY_OPTIONS}
											placeholder="Selecione a categoria"
											hasError={!!errors.category}
										/>
									)}
								</FormField>
							</div>
							<div className={generalStyles.row2}>
								<FormField label="Natureza do Produto" required={!isView} error={errors.nature}>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveLabel(FERTILIZER_NATURE_OPTIONS, form.nature)}
										</span>
									) : (
										<FormSelect
											value={form.nature}
											onChange={(v) => {
												set('nature', v);
												triggerChange({ ...form, nature: v });
											}}
											options={FERTILIZER_NATURE_OPTIONS}
											placeholder="Selecione a natureza"
											hasError={!!errors.nature}
										/>
									)}
								</FormField>
								<FormField label="Modo De Aplicação" required={!isView} error={errors.applicationMethods}>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveLabels(APPLICATION_METHODS, form.applicationMethods)}
										</span>
									) : (
										<FormSelect
											multiple
											value={form.applicationMethods}
											onChange={(v) => set('applicationMethods', v)}
											options={APPLICATION_METHODS}
											placeholder="Selecione o modo de aplicação"
											hasError={!!errors.applicationMethods}
										/>
									)}
								</FormField>
							</div>
							<div className={`${styles.toggleRow} ${isView ? styles.toggleDisabled : ''}`}>
								<UniversalToggle
									checked={form.active}
									onChangeToggle={(v) => !isView && set('active', v)}
								/>
								<span className={styles.toggleLabel}>{form.active ? 'Ativado' : 'Desativado'}</span>
							</div>
						</div>
					</div>

					{!isLime && (!isView || hasMacros || hasMicros) && (
						<div className={generalStyles.section}>
							<FormSectionTitle>Composição</FormSectionTitle>
							<div className={generalStyles.sectionFields}>
								{(!isView || hasMacros) && (
									<>
										<span className={styles.nutrientGroupTitle}>Macronutrientes</span>
										<div className={styles.nutrientGrid}>
											{visibleMacros.map(({ key, label }) => (
												<FormField key={key} label={label} error={errors[key]}>
													{isView ? (
														<span className={generalStyles.viewText}>
															{form[key as keyof FertilizerFormData]
																? `${form[key as keyof FertilizerFormData]} ${nutrientUnit}`
																: '—'}
														</span>
													) : (
														<NutrientInput
															value={form[key as keyof FertilizerFormData] as string}
															suffix={nutrientUnit}
															onChange={(e) => set(key as keyof FertilizerFormData, e.target.value)}
															hasError={!!errors[key]}
														/>
													)}
												</FormField>
											))}
										</div>
									</>
								)}
								{(!isView || hasMicros) && (
									<>
										<span className={styles.nutrientGroupTitle}>Micronutrientes</span>
										<div className={styles.nutrientGrid}>
											{MICROS.map(({ key, label }) => (
												<FormField key={key} label={label} error={errors[key]}>
													{isView ? (
														<span className={generalStyles.viewText}>
															{form[key as keyof FertilizerFormData]
																? `${form[key as keyof FertilizerFormData]} ${nutrientUnit}`
																: '—'}
														</span>
													) : (
														<NutrientInput
															value={form[key as keyof FertilizerFormData] as string}
															suffix={nutrientUnit}
															onChange={(e) => set(key as keyof FertilizerFormData, e.target.value)}
															hasError={!!errors[key]}
														/>
													)}
												</FormField>
											))}
										</div>
									</>
								)}
							</div>
						</div>
					)}

					{isLime && (
						<div className={generalStyles.section}>
							<FormSectionTitle>Corretivos</FormSectionTitle>
							<div className={generalStyles.sectionFields}>
								<div className={styles.nutrientGrid}>
									{CORRECTIVOS.map(({ key, label }) => (
										<FormField key={key} label={label} error={errors[key]}>
											{isView ? (
												<span className={generalStyles.viewText}>
													{form[key as keyof FertilizerFormData]
														? `${form[key as keyof FertilizerFormData]} ${nutrientUnit}`
														: '—'}
												</span>
											) : (
												<NutrientInput
													value={form[key as keyof FertilizerFormData] as string}
													suffix={nutrientUnit}
													onChange={(e) => set(key as keyof FertilizerFormData, e.target.value)}
													hasError={!!errors[key]}
												/>
											)}
										</FormField>
									))}
								</div>
							</div>
						</div>
					)}

					<div className={generalStyles.section}>
						<FormSectionTitle>Informações Complementares</FormSectionTitle>
						<div className={generalStyles.sectionFields}>
							<FormField label="Fração de Sais Solúveis" required={!isView} error={errors.solubleSaltFraction}>
								{isView ? (
									<span className={generalStyles.viewText}>
										{form.solubleSaltFraction ? `${form.solubleSaltFraction} %` : '—'}
									</span>
								) : (
									<NutrientInput
										value={form.solubleSaltFraction}
										suffix="%"
										onChange={(e) => set('solubleSaltFraction', e.target.value)}
										hasError={!!errors.solubleSaltFraction}
									/>
								)}
							</FormField>
						</div>
					</div>
				</div>
			</>
		);
	}
);
